import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, CircularProgress, Stack } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import type { Project, ProjectParticipant } from '@/services/projectTypes'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import ResultsHeader from '@/components/researcher/results/ResultsHeader'
import HeroPlayerTimeline from '@/components/researcher/results/HeroPlayerTimeline'
import AutoInsights from '@/components/researcher/results/AutoInsights'
import DonutCard from '@/components/researcher/results/DonutCard'
import AttentionCurveCard from '@/components/researcher/results/AttentionCurveCard'
import BenchmarkCard from '@/components/researcher/results/BenchmarkCard'
import QuestionResults from '@/components/researcher/results/QuestionResults'
import PerRespondentGrid from '@/components/researcher/results/PerRespondentGrid'
import MultiHero from '@/components/researcher/results/MultiHero'
import MultiSummaryTable from '@/components/researcher/results/MultiSummaryTable'
import {
  AUTO_INSIGHTS,
  MULTI_INSIGHTS,
  MULTI_N,
} from '@/components/researcher/results/resultsMockData'

type Variant = 'single' | 'multi'

/** Study results / analytics screen. Title + N come from the real Project API;
 *  all aggregated-emotion analytics are placeholders (see resultsMockData.ts).
 *  Mounted at /studies/:id/results where :id is a PROJECT id. */
export default function StudyResults() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const { id } = useParams()
  const projectId = Number(id)

  const [project, setProject] = useState<Project | null>(null)
  const [participants, setParticipants] = useState<ProjectParticipant[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [variant, setVariant] = useState<Variant>('single')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.allSettled([
      projectService.getProject(projectId),
      projectService.getProjectParticipants(projectId),
    ]).then(([projRes, partRes]) => {
      if (cancelled) return
      if (projRes.status === 'fulfilled') setProject(projRes.value)
      if (partRes.status === 'fulfilled') setParticipants(partRes.value)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [projectId])

  // Real N: completed participants, falling back to participants_count.
  const singleN = useMemo(() => {
    if (participants && participants.length > 0) {
      const completed = participants.filter((p) => p.status === 'completed').length
      if (completed > 0) return completed
      return participants.length
    }
    return project?.participants_count ?? 87
  }, [participants, project])

  const title = project?.name ?? t('header.eyebrow')

  if (loading) {
    return (
      <ResearcherShell active="studies" crumbs={[t('crumbs.studies'), title, t('crumbs.results')]}>
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360 }}>
          <CircularProgress sx={{ color: c.coral }} />
        </Box>
      </ResearcherShell>
    )
  }

  return (
    <ResearcherShell
      active="studies"
      crumbs={[t('crumbs.studies'), title, t('crumbs.results')]}
      onPrimaryAction={() => {}}
    >
      <Stack spacing={2.25} sx={{ maxWidth: 1340, mx: 'auto', px: { xs: 2, md: 0 } }}>
        {/* Variant toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 0.5,
              gap: 0.5,
              bgcolor: c.white,
              border: `1px solid ${c.ink08}`,
              borderRadius: `${theme.affexy.radii.pill}px`,
            }}
          >
            {(['single', 'multi'] as Variant[]).map((v) => {
              const active = variant === v
              return (
                <Box
                  key={v}
                  component="button"
                  type="button"
                  onClick={() => setVariant(v)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    border: 'none',
                    borderRadius: `${theme.affexy.radii.pill}px`,
                    cursor: 'pointer',
                    fontFamily: theme.affexy.fonts.body,
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    bgcolor: active ? alpha(c.coral, 0.1) : 'transparent',
                    color: active ? c.coral : c.ink56,
                  }}
                >
                  {t(`variant.${v}`)}
                </Box>
              )
            })}
          </Box>
        </Box>

        {variant === 'single' ? (
          <>
            <ResultsHeader title={title} n={singleN} onShare={() => {}} />
            <HeroPlayerTimeline />
            <AutoInsights ns="insights" items={AUTO_INSIGHTS} showSub />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2 }}>
              <DonutCard />
              <AttentionCurveCard />
              <BenchmarkCard />
            </Box>
            <QuestionResults />
            <PerRespondentGrid />
          </>
        ) : (
          <>
            <ResultsHeader title={title} n={MULTI_N} onShare={() => {}} />
            <MultiHero />
            <MultiSummaryTable />
            <AutoInsights ns="multi.insights" items={MULTI_INSIGHTS} />
          </>
        )}
      </Stack>
    </ResearcherShell>
  )
}
